// V6.46 — session cache for read-mostly reference data (store/display/hardware settings,
// categories, vendors, groups, lottery settings, permissions). Dozens of components fetch these
// on mount; now the first call hits the backend and everyone else shares the result for TTL ms.
// Any non-GET request to the same resource family invalidates its cache immediately, and the
// existing catalog-version poll calls invalidateApiCache("products") when the catalog changes.
import axios from "axios";

const TTL_MS = 5 * 60 * 1000;
const CACHED = [
  /\/api\/settings\/(store|display|hardware|receipt|tax|payment|pax|permissions\/mine)(\?|$)/,
  /\/api\/(categories|sub-categories|groups|manufacturers|vendors|departments)(\?|$)/,
  /\/api\/lottery\/settings\/public(\?|$)/,
  /\/api\/products(\?|$)/,
];
const store = new Map(); // key -> { at, response }
const inflight = new Map(); // key -> Promise

const family = (url) => (url.match(/\/api\/([a-z\-]+)(?:\/([a-z\-]+))?/) || [])[1] || "";
const keyOf = (cfg) => `${cfg.url}|${cfg.headers?.Authorization || cfg.headers?.authorization || ""}`;
const cacheable = (cfg) => (cfg.method || "get").toLowerCase() === "get" && CACHED.some((re) => re.test(cfg.url || "")) && !cfg.noCache;

export function invalidateApiCache(prefix) {
  for (const k of [...store.keys()]) if (!prefix || k.includes(`/api/${prefix}`)) store.delete(k);
}

export function installApiCache() {
  if (axios.__posCacheInstalled) return;
  axios.__posCacheInstalled = true;
  const base = axios.getAdapter(axios.defaults.adapter);
  axios.defaults.adapter = async (cfg) => {
    const method = (cfg.method || "get").toLowerCase();
    if (method !== "get") {
      const fam = family(cfg.url || "");
      if (fam) invalidateApiCache(fam === "settings" ? "settings" : fam);
      if (fam === "products" || fam === "categories" || fam === "vendors" || fam === "groups") invalidateApiCache("products");
      return base(cfg);
    }
    if (!cacheable(cfg)) return base(cfg);
    const key = keyOf(cfg);
    const hit = store.get(key);
    const clone = (r) => ({ ...r, data: typeof structuredClone === "function" ? structuredClone(r.data) : JSON.parse(JSON.stringify(r.data)), config: cfg, cached: true });
    if (hit && Date.now() - hit.at < TTL_MS) return clone(hit.response);
    if (inflight.has(key)) return inflight.get(key).then(clone);
    const p = base(cfg).then((r) => {
      if (r.status === 200) store.set(key, { at: Date.now(), response: { ...r, data: r.data } });
      inflight.delete(key);
      return r;
    }).catch((e) => { inflight.delete(key); throw e; });
    inflight.set(key, p);
    return p;
  };
}
